/**
 * The invoice ledger — `client/billing.html` §7, and (session 20) the studio's own
 * client detail.
 *
 * DESIGN.md P3: "9 invoices → the current one and the last two; 'all invoices →'."
 * The mockup shows three and folds five, and `VISIBLE_ROWS` is that three.
 *
 * What it renders is the row and nothing about the money story: the "$24,904.86
 * since March" sentence is the page's, because P2 gives the arithmetic one owner and
 * a table that also summed itself would be the second.
 *
 * **A state that the prose already says gets no chip** (CONTRACT.md §4.3): a paid
 * invoice prints its paid date and no "Paid" pill. What does get one is a row that
 * is *not* settled — `Tr variant="need"` for one still to pay, `"bad"` for a failed
 * charge — because those are the two the reader has to act on.
 *
 * **`onPay` is the one thing here that is not read-only**, and it is optional for a
 * reason: `MoneySection` (session 20's studio client detail) renders this same table
 * `links={false}` and never passes it — paying is the client's to do, never staff's,
 * so the studio's own read-only variant gets no `Pay` column at all rather than a
 * disabled one. `InvoicesSection` owns the click's whole story — the rail, the
 * confirm dialog, the mutation — and this table only ever calls back with the row.
 */

import type { ReactNode } from 'react'

import { isZero, type InvoiceView } from '@fareast/shared'

import { Fold } from '../portal/Disclosure.js'
import { ScrollX } from '../portal/Layout.js'
import { Button } from '../portal/Button.js'
import { Tag } from '../portal/Status.js'
import { Caption, TBody, THead, Table, Td, Th, Tr } from '../portal/Table.js'
import { B, DateText, DayText, More, Muted, Num, Ref, Xs } from '../portal/Text.js'
import { CardBody, CardMore } from '../portal/Card.js'
import { invoicePdfHref } from '../../api/client/reports.js'
import { useFormat } from '../../format/useFormat.js'

/** How many rows sit above the fold. DESIGN.md P3's "the current one and the last two". */
export const VISIBLE_ROWS = 3

/** `ach` → `ACH`, `sepa` → `SEPA`, `card` → `Card`, `wise` → `Wise`. */
export function railLabel(rail: string | null): string | null {
  if (rail === null) return null
  if (rail === 'ach' || rail === 'sepa') return rail.toUpperCase()
  return rail.charAt(0).toUpperCase() + rail.slice(1)
}

/**
 * Which row variant an invoice gets.
 *
 * Written as the states that still need something rather than as a list of ones to
 * ignore — `CLAUDE.md` §Gotchas: "A guard over 'has this already been decided' is
 * written as the states a row may still move **from**", and `INVOICE_STATUSES` gets
 * longer.
 *
 * **It takes the status, not the invoice**, because the status is all it reads. The
 * studio money page's invoice ledger is `MoneyLedgerRowSchema` — six fields, no
 * `due_on`, no `paid_at`, no `pdf_ready` — so with an `InvoiceView` parameter it
 * could not call this and wrote the same three lines again (`pages/studio/money/
 * state.ts` `ledgerVariant`, now gone). Two copies of "which invoices still want
 * something" is exactly the drift `INVOICE_STATUSES` getting longer would cause.
 */
export function rowVariant(invoice: { readonly status: string }): 'need' | 'bad' | undefined {
  if (invoice.status === 'failed') return 'bad'
  if (invoice.status === 'sent' || invoice.status === 'processing') return 'need'
  return undefined
}

export type InvoiceTableProps = {
  readonly invoices: readonly InvoiceView[]
  /** How many above the fold. `Infinity` shows every row and folds nothing. */
  readonly visible?: number
  /** The caption. Omitted when the section head already names the table (§5). */
  readonly caption?: string
  /** The line on the fold. */
  readonly foldSummary?: string
  /** Hide the PDF column — the studio's read-only variant does. */
  readonly links?: boolean
  /**
   * Renders a `Pay` button on every row `rowVariant` marks as still owed, and calls
   * back with that row's own invoice when it is pressed. Absent renders no `Pay`
   * column at all — the studio's read-only variant, and a client whose role or
   * payment method rules the action out, both leave this undefined rather than pass
   * a button that would only refuse.
   */
  readonly onPay?: ((invoice: InvoiceView) => void) | undefined
  /** Disables every `Pay` button — impersonation, or a payment already in flight. */
  readonly payDisabled?: boolean | undefined
  /**
   * A last column of controls, one cell per row, rendered by the caller.
   *
   * The studio's own five per-invoice moves (`components/studio/InvoiceMoves.tsx`)
   * go here, and they come in as a render slot rather than as five more callbacks
   * for the reason `onPay` is one callback: this table renders a row and knows
   * nothing about what may be done to it. It is deliberately *not* the same column
   * as `onPay` — paying is the client's and issuing, charging and refunding are the
   * studio's, and no reader ever sees both.
   */
  readonly actions?: ((invoice: InvoiceView) => ReactNode) | undefined
  /** The last column's heading. Required whenever `actions` is passed. */
  readonly actionsLabel?: string | undefined
}

export function InvoiceTable({
  invoices,
  visible,
  caption,
  foldSummary,
  links,
  onPay,
  payDisabled,
  actions,
  actionsLabel,
}: InvoiceTableProps) {
  const limit = visible ?? VISIBLE_ROWS
  const above = invoices.slice(0, limit)
  const below = invoices.slice(limit)
  const showLinks = links !== false

  return (
    <>
      <CardBody tight>
        <ScrollX>
          <Rows
            rows={above}
            caption={caption}
            links={showLinks}
            onPay={onPay}
            payDisabled={payDisabled}
            actions={actions}
            actionsLabel={actionsLabel}
          />
        </ScrollX>
      </CardBody>

      {below.length > 0 ? (
        <CardMore>
          <Fold
            summary={
              foldSummary ??
              `The other ${String(below.length)} invoice${below.length === 1 ? '' : 's'}`
            }
          >
            <ScrollX>
              <Rows
                rows={below}
                links={showLinks}
                onPay={onPay}
                payDisabled={payDisabled}
                actions={actions}
                actionsLabel={actionsLabel}
              />
            </ScrollX>
          </Fold>
        </CardMore>
      ) : null}
    </>
  )
}

/* ------------------------------------------------------------------ the table */

function Rows({
  rows,
  caption,
  links,
  onPay,
  payDisabled,
  actions,
  actionsLabel,
}: {
  readonly rows: readonly InvoiceView[]
  readonly caption?: string | undefined
  readonly links: boolean
  readonly onPay?: ((invoice: InvoiceView) => void) | undefined
  readonly payDisabled?: boolean | undefined
  readonly actions?: ((invoice: InvoiceView) => ReactNode) | undefined
  readonly actionsLabel?: string | undefined
}) {
  const format = useFormat()

  return (
    <Table>
      {caption === undefined ? null : <Caption>{caption}</Caption>}
      <THead>
        <Tr>
          <Th scope="col">Ref</Th>
          <Th scope="col">Period</Th>
          <Th scope="col">Issued</Th>
          <Th scope="col">Due</Th>
          <Th scope="col">Paid</Th>
          <Th scope="col">Rail</Th>
          <Th scope="col" right>
            Total
          </Th>
          {links ? (
            <Th scope="col" right>
              Files
            </Th>
          ) : null}
          {onPay === undefined ? null : (
            <Th scope="col" right>
              Pay
            </Th>
          )}
          {actions === undefined ? null : (
            <Th scope="col" right>
              {actionsLabel ?? 'Do'}
            </Th>
          )}
        </Tr>
      </THead>
      <TBody>
        {rows.map((invoice, index) => {
          const rail = railLabel(invoice.rail_expected)
          return (
            <Tr key={invoice.ref} variant={rowVariant(invoice)}>
              <Td>
                <Ref value={invoice.ref} strong={index === 0} nowrap />
              </Td>
              {/*
                `scope` first, because it is the sentence the invoice itself carries
                and the period is only its month. `FES-2026-011` is "April 2026 ·
                signup", and printing `format.month(period)` over the top of it lost
                the word that says why a retainer's first invoice is not a month like
                the others. The period is the fallback for a row whose scope is
                empty, and the whole cell falls back to the scope when there is no
                period at all — a milestone invoice has one and not the other.
              */}
              <Td>
                {invoice.scope !== ''
                  ? invoice.scope
                  : invoice.period === null
                    ? null
                    : format.month(invoice.period)}
              </Td>
              <Td date>
                {invoice.issued_at === null ? (
                  <Muted>not issued</Muted>
                ) : (
                  /*
                   * Read in **UTC**, like the due date beside it and unlike a call.
                   *
                   * An invoice's date is a day the studio names, not an instant the
                   * reader was present for: the billing run stamps `FES-2026-047` at
                   * 03:00:30Z on the 1st, which is a New York reader's 31 July. The
                   * mockup prints "1 Aug 2026" and so does every other reader of that
                   * row — the studio's own zone would be better still, and the field
                   * that would carry it (`issued_on`, a `DateOnly`) is in the session
                   * report.
                   */
                  <DateText value={invoice.issued_at} zone="UTC" />
                )}
              </Td>
              <Td date>
                {invoice.due_on === null ? (
                  <Muted>—</Muted>
                ) : (
                  // A due date is a **day**, not an instant. `DayText` is the one
                  // place that conversion lives — twenty sites wrote it out.
                  <DayText value={invoice.due_on} />
                )}
              </Td>
              <Td date>
                {invoice.paid_at === null ? (
                  <Muted>—</Muted>
                ) : (
                  // The day it settled, for the same reason as the issue date above.
                  <DateText value={invoice.paid_at} zone="UTC" />
                )}
              </Td>
              {/*
                No rail is a **fact about the invoice**, so the cell says it rather
                than being blank.

                `rail_expected` is `client.payment_rail_pref` read at the instant the
                invoice was issued and never rewritten afterwards (an invoice is never
                edited), so null means the client had no method on file that day and
                the invoice was payable by transfer only. Casaturca's `FES-2026-011` is
                exactly that: issued 4 March, two days before the ACH mandate was
                verified on the 6th, and settled by transfer on the 9th. Session 24
                (R3-27) read the empty cell beside "paid 9 Mar 2026" as the page
                failing to look something up, which is the one thing it was not doing.
              */}
              <Td>{rail === null ? <Muted>none on file</Muted> : <Tag>{rail}</Tag>}</Td>
              <Td right num>
                {index === 0 ? (
                  <B>
                    <Num value={invoice.total} />
                  </B>
                ) : (
                  <Num value={invoice.total} />
                )}
                <WrittenOff invoice={invoice} />
              </Td>
              {links ? (
                <Td right nowrap>
                  {invoice.pdf_ready ? (
                    <More href={invoicePdfHref(invoice.ref)}>PDF</More>
                  ) : (
                    <Muted>rendering</Muted>
                  )}
                </Td>
              ) : null}
              {actions === undefined ? null : <Td right>{actions(invoice)}</Td>}
              {onPay === undefined ? null : (
                <Td right nowrap>
                  {rowVariant(invoice) === undefined ? null : (
                    <Button
                      size="sm"
                      variant="ochre"
                      disabled={payDisabled === true}
                      aria-label={`Pay ${invoice.ref}`}
                      onClick={() => {
                        onPay(invoice)
                      }}
                    >
                      Pay
                    </Button>
                  )}
                </Td>
              )}
            </Tr>
          )
        })}
      </TBody>
    </Table>
  )
}

/**
 * "$445.00 written off", muted, as a second line under the total — or nothing.
 *
 * `.docs/44-invoice-write-off.md` decision 6: the invoice and its PDF are never
 * edited, so the **total stays what was issued** and the write-off is stated beside
 * it rather than taken off it. A reader comparing this row with their PDF sees the
 * same figure on both, and the line under it is the one thing the PDF cannot say.
 * The reason is not here: a ledger row has one line, `InvoiceView` does not carry it,
 * and it travels on the invoice's own detail read (`InvoiceDetailSchema.write_offs`).
 *
 * `written_off` is the server's sum of the invoice's write-off rows — this adds
 * nothing up. Zero for almost every invoice, and zero draws no line at all: a muted
 * "$0.00 written off" under eight totals is chrome that has not earned its place
 * (DESIGN.md P7).
 *
 * The **studio's** client detail renders this same table, so a manager reading
 * Halcyon's ledger sees the line the client sees.
 */
function WrittenOff({ invoice }: { readonly invoice: InvoiceView }) {
  if (isZero(invoice.written_off)) return null
  return (
    <Xs block>
      <Muted>
        <Num value={invoice.written_off} /> written off
      </Muted>
    </Xs>
  )
}
